import { apiClient, ApiResponse } from './api-client'
import type { MenuItem } from '../models/menu'

export interface CreateMenuItemData {
  categoryId: number
  name: string
  description?: string
  price: number
}

export interface UpdateMenuItemData {
  name?: string
  description?: string
  price?: number
  isAvailable?: boolean
}

export class MenuItemService {
  /**
   * Get items by category
   */
  async getItems(categoryId: string): Promise<MenuItem[]> {
    const response = await apiClient.get<ApiResponse<{ items: MenuItem[] }>>(
      `/menus/items/category/${categoryId}`
    )
    if (response.data.success && response.data.data) {
      return response.data.data.items || []
    }
    throw new Error(response.data.error?.message || 'Failed to get items')
  }

  /**
   * Get all items by business
   */
  async getItemsByBusiness(businessId: string): Promise<MenuItem[]> {
    const response = await apiClient.get<ApiResponse<{ items: MenuItem[] }>>(
      `/menus/items/business/${businessId}`
    )
    if (response.data.success && response.data.data) {
      return response.data.data.items || []
    }
    throw new Error(response.data.error?.message || 'Failed to get items')
  }

  /**
   * Create new menu item
   */
  async createItem(data: CreateMenuItemData): Promise<MenuItem> {
    const response = await apiClient.post<ApiResponse<{ item: MenuItem }>>('/menus/items', data)
    if (response.data.success && response.data.data) {
      return response.data.data.item
    }
    throw new Error(response.data.error?.message || 'Failed to create item')
  }

  /**
   * Update menu item
   */
  async updateItem(id: string, data: UpdateMenuItemData): Promise<MenuItem> {
    const response = await apiClient.put<ApiResponse<{ item: MenuItem }>>(`/menus/items/${id}`, data)
    if (response.data.success && response.data.data) {
      return response.data.data.item
    }
    throw new Error(response.data.error?.message || 'Failed to update item')
  }

  /**
   * Delete menu item
   */
  async deleteItem(id: string): Promise<void> {
    try {
      const response = await apiClient.delete<ApiResponse>(`/menus/items/${id}`)
      if (!response.data.success) {
        const errorMessage = response.data.error?.message || 'فشل حذف الصنف'
        const error = new Error(errorMessage)
        ;(error as any).response = { data: response.data }
        throw error
      }
    } catch (error: any) {
      // Preserve the original error response if available
      if (error.response) {
        throw error
      }
      // If it's a network error or other issue, wrap it
      const errorMessage = error.message || 'حدث خطأ أثناء حذف الصنف'
      const newError = new Error(errorMessage)
      ;(newError as any).response = error.response
      throw newError
    }
  }

  /**
   * Toggle item availability
   */
  async toggleItem(id: string): Promise<MenuItem> {
    const response = await apiClient.patch<ApiResponse<{ item: MenuItem }>>(`/menus/items/${id}/toggle`)
    if (response.data.success && response.data.data) {
      return response.data.data.item
    }
    throw new Error(response.data.error?.message || 'Failed to toggle item')
  }

  /**
   * Upload item image
   */
  async uploadItemImage(id: string, file: File): Promise<string> {
    const formData = new FormData()
    formData.append('image', file)
    const response = await apiClient.post<ApiResponse<{ imageUrl: string }>>(
      `/menus/items/${id}/image`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    )
    if (response.data.success && response.data.data) {
      return response.data.data.imageUrl
    }
    throw new Error(response.data.error?.message || 'Failed to upload image')
  }

  /**
   * Remove item image
   */
  async removeItemImage(id: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse>(`/menus/items/${id}/image`)
    if (!response.data.success) {
      throw new Error(response.data.error?.message || 'Failed to remove image')
    }
  }

}

// Export singleton instance
export const menuItemService = new MenuItemService()

